import React, { useRef,  useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import {
  getGates,
  createGates,
  updateGate,
  deleteGate
} from '../services/gateService';
import { getAirportsSelection } from '../services/poiServices';
import { getConcourses } from '../services/airportService';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { useRefresh } from '../utils/RefreshContext';

const Svg = ({ d, ...props }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8" strokeLinecap="round" {...props}>
    <path d={d} />
  </svg>
);

const GateManagement = () => {

  const { refreshKey } = useRefresh();
  const [gates, setGates] = useState([]);
  const [airports, setAirports] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [concourses, setConcourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [airportSearch, setAirportSearch] = useState('');
  const [isAirportDropdownOpen, setIsAirportDropdownOpen] = useState(false);
  const [isAirportSearching, setIsAirportSearching] = useState(false);
  const airportDropdownRef = useRef(null);

  // Custom Modal State
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Pagination & Filter States
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [filterAirport, setFilterAirport] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [formData, setFormData] = useState({
    gate_number: '',
    airport_id: '',
    concourse_id: '',
    lat: '',
    lng: '',
    status: 'Active',
    airline_ids: [],
    notes: ''
  });

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const res = await getAirportsSelection();
        if (res && res.result) {
          setAirports(res.data || []);
        }
      } catch (err) { console.error(err); }
    };
    fetchMeta();
  }, []);
  const prevSearch = useRef(search);

  useEffect(() => {
    if (!isAirportSearching) return;
    if (airportSearch.length < 3) return;

    const timer = setTimeout(async () => {
      try {
        const res = await getAirportsSelection({ search: airportSearch });
        if (res && res.result) setAirports(res.data);
      } catch (err) {
        console.error(err);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [airportSearch, isAirportSearching]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (airportDropdownRef.current && !airportDropdownRef.current.contains(event.target)) {
        setIsAirportDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchChanged = prevSearch.current !== search;
    prevSearch.current = search;

    const timer = setTimeout(() => {
      if (searchChanged && page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    }, searchChanged ? 500 : 0);

    return () => clearTimeout(timer);
  }, [page, limit, filterAirport, filterStatus, refreshKey, search]);


  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search: search || undefined,
        airport_id: filterAirport === 'All' ? undefined : filterAirport,
        status: filterStatus === 'All' ? undefined : filterStatus
      };
      const res = await getGates(params);
      setGates(res?.data || []);
      setTotalPages(res?.meta?.totalPages || 1);
      setTotalRecords(res?.meta?.total || 0);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error("Failed to load gates");
    } finally {
      setLoading(false);
    }
  };

  const handleAirportChange = async (airportId) => {
    const selectedAirport = airports.find(a => String(a.id) === String(airportId));
    setFormData({ ...formData, airport_id: airportId, concourse_id: '' });
    setAirportSearch(selectedAirport ? `${selectedAirport.code} — ${selectedAirport.name}` : '');
    setIsAirportSearching(false);
    setIsAirportDropdownOpen(false);

    if (airportId) {
      try {
        const res = await getConcourses(airportId);
        setConcourses(res?.data || []);
      } catch (err) { console.error(err); }
    } else {
      setConcourses([]);
    }
  };

  const handleAirlineToggle = (id) => {
    const stringId = String(id);
    const current = [...formData.airline_ids.map(val => String(val))];
    if (current.includes(stringId)) {
      setFormData({ ...formData, airline_ids: current.filter(item => item !== stringId) });
    } else {
      setFormData({ ...formData, airline_ids: [...current, stringId] });
    }
  };

  const handleEdit = async (gate) => {
    setIsEditing(true);
    setCurrentId(gate.id);
    
    // Fetch concourses for this airport first
    if (gate.airport_id) {
      try {
        const res = await getConcourses(gate.airport_id);
        setConcourses(res?.data || []);
      } catch (err) { console.error(err); }
    }

    setFormData({
      gate_number: gate.gate_number || '',
      airport_id: gate.airport_id || '',
      concourse_id: gate.concourse_id || '',
      lat: gate.lat || '',
      lng: gate.lng || '',
      status: gate.status || 'Active',
      airline_ids: gate.airlines?.map(al => al.id) || [],
      notes: gate.notes || ''
    });
    const selectedAirport = gate.airport || gate.airport_details;
    if (selectedAirport) {
      setAirportSearch(`${selectedAirport.code || ''} — ${selectedAirport.name || ''}`.replace(/^ — /, '').trim());
      setAirports(prev => {
        if (!prev.find(a => a.id === selectedAirport.id)) {
          return [selectedAirport, ...prev];
        }
        return prev;
      });
    }
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmDelete = (gate) => {
    setDeleteTarget(gate);
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteGate(deleteTarget.id);
      toast.success("Gate deleted successfully");
      setShowConfirm(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.gate_number || !formData.airport_id || !formData.concourse_id || !formData.lat || !formData.lng) {
      return toast.warning("Please fill required fields");
    }

    setSubmitting(true);
    try {
      // Remove airline_ids from payload as requested
      const { airline_ids, ...payload } = formData;
      
      const response = isEditing 
        ? await updateGate(currentId, payload)
        : await createGates(payload);

      if (response && (response.status === 200 || response.status === 201)) {
        toast.success(`Gate ${isEditing ? 'updated' : 'created'} successfully`);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      gate_number: '',
      airport_id: '',
      concourse_id: '',
      lat: '',
      lng: '',
      status: 'Active',
      airline_ids: [],
      notes: ''
    });
    setConcourses([]);
    setAirportSearch('');
    setIsAirportDropdownOpen(false);
    setIsAirportSearching(false);
    setIsEditing(false);
    setCurrentId(null);
    setShowForm(false);
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (loading && gates.length === 0) return <Spinner />;

  return (
    <div>
      <Topbar title="Gate Management" subtitle="Monitor and manage airport terminal gates and assignments" />
      
      {/* CUSTOM CONFIRMATION MODAL */}
      {showConfirm && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, backdropFilter: 'blur(4px)' }}>
          <div className="modal-card" style={{ background: 'white', padding: 32, borderRadius: 20, width: '100%', maxWidth: 400, textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ width: 64, height: 64, background: '#FFF5F5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--dark)', marginBottom: 10 }}>Confirm Deletion</h3>
            <p style={{ fontSize: 14, color: 'var(--grey)', marginBottom: 24, lineHeight: 1.5 }}>
              Are you sure you want to delete this gate? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1, background: '#F87171', borderColor: '#F87171' }} onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="content">
        <div className="screen-label">5.4 — Gate Management</div>

        <div className="card">
          {/* FORM SECTION */}
          {(showForm || isEditing) && (
            <div style={{ borderBottom: '2px solid #F0F4F8', padding: '24px 26px', background: '#F8FAFC', borderRadius: '20px 20px 0 0' }}>
              <div className="screen-label" style={{ marginBottom: 18, fontSize: 13, color: 'var(--blue)', fontWeight: 700 }}>{isEditing ? 'Modify Existing Gate' : 'Add New Terminal Gate'}</div>
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Gate Number <span className="req">*</span></label>
                    <input
                      className="form-input"
                      placeholder='e.g. "A2"'
                      value={formData.gate_number}
                      onChange={e => setFormData({ ...formData, gate_number: e.target.value })}
                      style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Airport <span className="req">*</span></label>
                    <div className="searchable-select" ref={airportDropdownRef}>
                      <div className="ss-input-wrap" onClick={() => setIsAirportDropdownOpen(!isAirportDropdownOpen)}>
                        <input
                          className="form-input"
                          placeholder="Search airport..."
                          value={airportSearch}
                          onChange={(e) => {
                            setAirportSearch(e.target.value);
                            setIsAirportSearching(true);
                            setIsAirportDropdownOpen(true);
                          }}
                          onFocus={() => setIsAirportDropdownOpen(true)}
                          required
                          style={{ width: '100%' }}
                        />
                        <svg className={`ss-arrow ${isAirportDropdownOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </div>
                      {isAirportDropdownOpen && (
                        <div className="ss-dropdown">
                          {airports.length > 0 ? (
                            airports.map(ap => (
                              <div
                                key={ap.id}
                                className={`ss-option ${String(formData.airport_id) === String(ap.id) ? 'selected' : ''}`}
                                onClick={() => handleAirportChange(ap.id)}
                              >
                                {ap.code} — {ap.name}
                              </div>
                            ))
                          ) : (
                            <div className="ss-no-results">No airports found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Concourse / Zone <span className="req">*</span></label>
                    <select
                      className="form-select"
                      value={formData.concourse_id}
                      onChange={e => setFormData({ ...formData, concourse_id: e.target.value })}
                      disabled={!formData.airport_id}
                    >
                      <option value="">Select Concourse</option>
                      {concourses.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status <span className="req">*</span></label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={e => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="Active">Active</option>
                      <option value="Temporarily Closed">Temporarily Closed</option>
                      <option value="Under Construction">Under Construction</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Longitude <span className="req">*</span></label>
                    <input
                      className="form-input"
                      placeholder="e.g. -82.88550"
                      value={formData.lng}
                      onChange={e => setFormData({ ...formData, lng: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Latitude <span className="req">*</span></label>
                    <input
                      className="form-input"
                      placeholder="e.g. 39.99690"
                      value={formData.lat}
                      onChange={e => setFormData({ ...formData, lat: e.target.value })}
                    />
                  </div>
                  {/* 
                  <div className="form-group">
                    <label className="form-label">Assign Airlines <span className="opt">Optional — Multi-select</span></label>
                    <div className="multi-select" style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 8, 
                      padding: '10px 14px', 
                      border: '1.5px solid #E8EDF3', 
                      borderRadius: 12, 
                      background: 'white',
                      minHeight: 46,
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      {formData.airline_ids.map(id => {
                        const al = airlines.find(a => String(a.id) === String(id));
                        return (
                          <div key={id} className="ms-pill" style={{ 
                            background: '#F0F7FF', 
                            color: '#0078D2', 
                            padding: '6px 12px', 
                            borderRadius: '8px', 
                            fontSize: '13px', 
                            fontWeight: '700', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            border: '1px solid #D1E9FF'
                          }}>
                            {al ? (al.iata || al.code || al.name || al.title || `ID: ${id}`) : `ID: ${id}`} 
                            <span 
                              onClick={() => handleAirlineToggle(id)} 
                              style={{ cursor: 'pointer', fontSize: '16px', color: '#94A3B8', marginLeft: '4px', display: 'inline-flex' }}
                            >×</span>
                          </div>
                        );
                      })}
                      
                      <div style={{ position: 'relative', flex: 1, minWidth: 120 }}>
                        <select 
                          className="form-select" 
                          style={{ 
                            border: 'none', 
                            background: 'transparent', 
                            padding: '0 8px', 
                            height: '100%', 
                            width: '100%',
                            fontSize: '13px', 
                            fontWeight: '600', 
                            color: '#94A3B8',
                            outline: 'none',
                            appearance: 'none',
                            cursor: 'pointer'
                          }}
                          onChange={(e) => {
                            if (e.target.value) {
                              handleAirlineToggle(e.target.value);
                              e.target.value = '';
                            }
                          }}
                        >
                          <option value="">+ Assign airline</option>
                          {airlines
                            .filter(al => !formData.airline_ids.map(val => String(val)).includes(String(al.id)))
                            .map(al => (
                              <option key={al.id} value={al.id}>{al.iata || al.code} — {al.name}</option>
                            ))
                          }
                        </select>
                        <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#94A3B8' }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9l6 6 6-6"/></svg>
                        </div>
                      </div>
                    </div>
                  </div> 
                  */}
                  <div className="form-group full">
                    <label className="form-label">Notes <span className="opt">Optional</span></label>
                    <input
                      className="form-input"
                      placeholder='e.g. "Near food court B"'
                      value={formData.notes}
                      onChange={e => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-footer" style={{ paddingLeft: 0, paddingRight: 0, marginTop: 24 }}>
                  <button type="button" className="btn btn-outline" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /></svg>
                    {submitting ? 'Saving...' : isEditing ? 'Update Gate' : 'Save Gate'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="toolbar">
            <div className="search-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
              <input
                className="search-input"
                placeholder="Search gates..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select
              className="filter-select"
              value={filterAirport}
              onChange={e => setFilterAirport(e.target.value)}
            >
              <option value="All">All Airports</option>
              {airports.map(ap => (
                <option key={ap.id} value={ap.id}>{ap.code}</option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Temporarily Closed">Closed</option>
              <option value="Under Construction">Construction</option>
            </select>
            {!showForm && (
              <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true); }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>Add Gate</button>
            )}
          </div>

          <table className="tbl">
            <thead>
              <tr>
                <th>Gate</th>
                <th>Airport</th>
                <th>Concourse</th>
                <th>Coordinates</th>
                {/* <th>Airlines</th> */}
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}><Spinner /></td></tr>
              ) : gates.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>No gates found</td></tr>
              ) : (
                gates.map((g) => (
                  <tr key={g.id}>
                    <td><span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>{g.gate_number}</span></td>
                    <td><span className="tag">{g.airport?.code}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--grey)' }}>{g.concourse?.name}</td>
                    <td style={{ fontSize: 11, color: 'var(--grey)', fontFamily: "'Space Grotesk', sans-serif" }}>
                      {g.lat}, {g.lng}
                    </td>
                    {/* <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {g.airlines?.map(al => (
                          <div key={al.id} style={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: 6, 
                            background: '#0078D2', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            fontSize: 8, 
                            fontWeight: 800, 
                            color: 'white' 
                          }}>
                            {al.iata}
                          </div>
                        ))}
                      </div>
                    </td> */}
                    <td><span className={`badge badge-${g.status === 'Active' ? 'active' : g.status === 'Under Construction' ? 'construction' : 'closed'}`}>{g.status}</span></td>
                    <td>
                      <div className="action-btns">
                        <button className="action-btn" onClick={() => handleEdit(g)}><Svg d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></button>
                        <button className="action-btn danger" onClick={() => confirmDelete(g)}><Svg d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* PAGINATION */}
          {gates.length > 0 && (
            <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 26px', borderTop: '1px solid #F0F4F8' }}>
              <div style={{ fontSize: 13, color: 'var(--grey)' }}>
                Showing <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{((page - 1) * limit) + 1}</span> to <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{Math.min(page * limit, totalRecords)}</span> of <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{totalRecords}</span> entries
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-outline"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', gap: 4 }}>
                  {renderPagination().map((p, i) => (
                    <button
                      key={i}
                      className={`btn ${page === p ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => typeof p === 'number' && setPage(p)}
                      style={{
                        padding: '6px 12px',
                        fontSize: 12,
                        minWidth: 36,
                        cursor: typeof p === 'number' ? 'pointer' : 'default',
                        border: typeof p === 'number' ? '' : 'none',
                        background: typeof p === 'number' ? '' : 'transparent'
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <button
                  className="btn btn-outline"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GateManagement;
